import React, { useState } from 'react';
import {
    Box, Typography, Grid, Button, Dialog, DialogContent, styled,
    alpha
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { getMediaUrl } from '../../../utils/urlHelper';
import { GlassCard } from '../../StyledComponents/StyledComponent';

const DocumentCard = styled(GlassCard)(({ theme }) => ({
    padding: '24px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    background: theme.palette.mode === 'dark' ? alpha('#1e293b', 0.4) : alpha('#ffffff', 0.5),
    '&:hover': {
        borderColor: '#3DAC5A',
        boxShadow: `0 12px 24px ${alpha('#3DAC5A', 0.15)}`,
        transform: 'translateY(-4px)',
    },
}));

const DocumentLink = styled('a')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#3DAC5A',
    textDecoration: 'none',
    padding: '8px 12px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    backgroundColor: 'rgba(61, 172, 90, 0.05)',
    '&:hover': {
        backgroundColor: 'rgba(61, 172, 90, 0.15)',
        transform: 'translateY(-2px)',
    },
}));

const PreviewPlaceholder = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '120px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    border: '1px solid #eee',
    overflow: 'hidden',
    position: 'relative',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    '&:hover .zoom-overlay': {
        opacity: 1,
    }
}));

const ZoomOverlay = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    cursor: 'pointer',
});

const DocumentsTab = ({ documents, onUploadInitiated, formData }) => {
    const [previewImage, setPreviewImage] = useState(null);

    const documentCategories = [
        {
            group: 'Identity',
            items: [
                { key: 'aadhar_card', label: 'Aadhar Card', icon: <PersonIcon /> },
                { key: 'pan_card', label: 'PAN Card', icon: <PersonIcon /> }
            ]
        },
        {
            group: 'Employment',
            items: [
                { key: 'offer_letter', label: 'Offer Letter', icon: <WorkIcon /> },
                { key: 'resignation_acceptance_letter', label: 'Resignation Letter', icon: <WorkIcon /> },
                { key: 'experience_letter', label: 'Experience Letter', icon: <WorkIcon /> },
                { key: 'relieving_letter', label: 'Relieving Letter', icon: <WorkIcon /> },
                { key: 'resume', label: 'Resume', icon: <InsertDriveFileIcon /> }
            ]
        },
        {
            group: 'Education',
            items: [
                { key: 'sslc_certificate', label: 'SSLC Certificate', icon: <DescriptionIcon /> },
                { key: 'intermediate_certificate', label: 'Intermediate Certificate', icon: <DescriptionIcon /> },
                { key: 'graduation_certificate', label: 'Graduation Certificate', icon: <DescriptionIcon /> },
                { key: 'pg_certificate', label: 'PG Certificate', icon: <DescriptionIcon /> },
                { key: 'nicsm_certificate', label: 'NISM Certificate', icon: <DescriptionIcon /> }
            ]
        },
        {
            group: 'Finance',
            items: [
                { key: 'bank_statement_last_6_months', label: 'Bank Statement', icon: <AccountBalanceIcon /> },
                { key: 'pay_slips_last_6_months', label: 'Previous Pay Slips', icon: <ReceiptIcon /> }
            ]
        }
    ];

    const currentDocs = documents || {};

    return (
        <Box>
            {documentCategories.map((category) => (
                <Box key={category.group} sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {category.group}
                    </Typography>
                    <Grid container spacing={3}>
                        {category.items.map((item) => {
                            const docPath = currentDocs[item.key];
                            const isUploaded = !!docPath;
                            const fileUrl = getMediaUrl(docPath);
                            const isImage = docPath && (docPath.toLowerCase().endsWith('.jpg') || docPath.toLowerCase().endsWith('.jpeg') || docPath.toLowerCase().endsWith('.png'));

                            return (
                                <Grid item xs={12} sm={6} md={3} key={item.key}>
                                    <DocumentCard sx={{
                                        border: isUploaded ? '1px solid rgba(61, 172, 90, 0.3)' : '1px dashed #ccc',
                                        backgroundColor: isUploaded ? 'rgba(61, 172, 90, 0.02)' : '#fff'
                                    }}>
                                        <PreviewPlaceholder>
                                            {isUploaded ? (
                                                isImage ? (
                                                    <>
                                                        <img src={fileUrl} alt={item.label} />
                                                        <ZoomOverlay className="zoom-overlay" onClick={() => setPreviewImage(fileUrl)}>
                                                            <ZoomInIcon />
                                                        </ZoomOverlay>
                                                    </>
                                                ) : (
                                                    <InsertDriveFileIcon sx={{ fontSize: 48, color: '#3DAC5A' }} />
                                                )
                                            ) : (
                                                <Box sx={{ textAlign: 'center', opacity: 0.5 }}>
                                                    {item.icon}
                                                    <Typography variant="caption" display="block">Missing</Typography>
                                                </Box>
                                            )}
                                        </PreviewPlaceholder>

                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, flexGrow: 1 }}>
                                            {item.label}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {isUploaded ? (
                                                <>
                                                    <DocumentLink href={fileUrl} target="_blank" rel="noopener noreferrer">
                                                        <VisibilityIcon fontSize="small" />
                                                        View
                                                    </DocumentLink>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => onUploadInitiated(item.key)}
                                                        sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.8rem' }}
                                                    >
                                                        Replace
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => onUploadInitiated(item.key)}
                                                    sx={{
                                                        backgroundColor: '#3DAC5A',
                                                        '&:hover': { backgroundColor: '#2d8a47' },
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    Upload
                                                </Button>
                                            )}
                                        </Box>
                                    </DocumentCard>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            ))}

            {/* Document Collection Section */}
            {formData && (
                <Box sx={{ mt: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Document Collection
                    </Typography>
                    <GlassCard sx={{
                        p: 4,
                        border: `1px solid #3DAC5A`,
                        textAlign: 'center',
                        maxWidth: '800px',
                        mx: 'auto',
                        background: alpha('#3DAC5A', 0.02),
                        mb: 5
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 32, color: '#3DAC5A' }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                Secure Onboarding Invitation
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                            Secure links expire after 14 days and are not stored in readable form. Use the Onboarding page to send or replace the employee invitation.
                        </Typography>

                        <Box sx={{
                            p: 2,
                            mb: 3,
                            backgroundColor: alpha('#000', 0.03),
                            borderRadius: '12px',
                            border: `1px solid ${alpha('#000', 0.05)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2
                        }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontWeight: 700, color: '#3DAC5A', flex: 1, textAlign: 'left' }}>
                                {formData.form_url || 'No active link is displayed after issuance.'}
                            </Typography>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={!formData.form_url}
                                onClick={() => {
                                    if (formData.form_url) {
                                        navigator.clipboard.writeText(formData.form_url);
                                        // Use a more modern notification if available
                                        alert("URL copied to clipboard!");
                                    }
                                }}
                                sx={{
                                    bgcolor: '#3DAC5A',
                                    color: '#fff',
                                    '&:hover': { bgcolor: '#2d8a47' },
                                    textTransform: 'none',
                                    px: 3,
                                    borderRadius: '8px',
                                    fontWeight: 700
                                }}
                            >
                                Copy
                            </Button>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                disabled={!formData.form_url}
                                onClick={() => window.open(formData.form_url, '_blank')}
                                sx={{
                                    borderColor: '#3DAC5A',
                                    color: '#3DAC5A',
                                    '&:hover': { borderColor: '#2d8a47', bgcolor: alpha('#3DAC5A', 0.05) },
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    px: 4,
                                    py: 1,
                                    fontWeight: 700,
                                }}
                            >
                                Open Form
                            </Button>
                        </Box>
                    </GlassCard>
                </Box>
            )}

            {/* Zoomed Preview Dialog */}
            <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="lg">
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <Button
                        onClick={() => setPreviewImage(null)}
                        sx={{ position: 'absolute', top: 10, right: 10, color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
                    >
                        Close
                    </Button>
                    <img src={previewImage} alt="Large Preview" style={{ width: '100%', display: 'block' }} />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default DocumentsTab;
